import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { FormService } from "../../services/form.service";
import { TrackingService } from "../../services/tracking.service";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { ConsentMoversModalComponent } from "../../form-parts/consent-movers-modal/consent-movers-modal.component";
import { atLeastOneMoverSelected } from '../../services/custom-validators';

@Component( {
	selector: 'app-consent-movers',
	templateUrl: './consent-movers.component.html',
	styleUrls: ['./consent-movers.component.scss']
} )
export class ConsentMoversComponent implements OnDestroy {

	consentMovers !: UntypedFormGroup
	formGroupName : string = 'consentMovers'
	readonly maxDisplayed: number = 5
	public displayedIndices: number[] = []

	// Verified movers: everything is gated on the response actually carrying the flag,
	// so until ask-prematch returns it the step looks exactly as it does today
	public hasVerifiedMovers: boolean = false
	public explainerOpen: boolean = false

	@ViewChild( 'verifiedExplainer' ) private verifiedExplainerRef?: ElementRef<HTMLElement>
	@ViewChild( 'verifiedExplainerTrigger' ) private verifiedExplainerTriggerRef?: ElementRef<HTMLButtonElement>

	// Kept as a property so the same reference can be removed again on destroy
	private readonly closeExplainerOnScroll = (): void => {

		if( this.explainerOpen ) {

			this.explainerOpen = false;
		}
	}

	constructor(
		public formService: FormService,
		private trackingService: TrackingService,
		private dialog: MatDialog
	) {
	}

	public ngOnInit(): void {

		this.consentMovers = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		this.formService.moversConsentUpdate$.subscribe( () => {

			this.updateDisplayedMovers();

			// Add validation after movers are updated
			const moversArray = this.allMovers;
			this.consentMovers.setValidators(atLeastOneMoverSelected());
			this.consentMovers.updateValueAndValidity();

			// This subscription is the only point where the movers array changes
			this.hasVerifiedMovers = this.allMovers.controls.some( control => !! control.get( '_verified' )?.value );
		} );

		// Capture mode: on mobile the step host is itself the scroll container
		window.addEventListener( 'scroll', this.closeExplainerOnScroll, true );
	}

	public ngOnDestroy(): void {

		window.removeEventListener( 'scroll', this.closeExplainerOnScroll, true );
	}

	public toggleExplainer(): void {

		this.explainerOpen = ! this.explainerOpen;

		if( this.explainerOpen ) {

			this.formService.trackEventWithContext( 'Prematch - Verified explainer', 'consentMovers' );
		}
	}

	public closeExplainer(): void {

		this.explainerOpen = false;
	}

	@HostListener( 'document:click', ['$event'] )
	public handleExplainerOutsideClick( event: MouseEvent ): void {

		if( ! this.explainerOpen || ! this.verifiedExplainerRef ) {

			return;
		}

		// The trigger lives inside the wrapper, so the click that opens the panel
		// does not immediately close it again
		if( ! this.verifiedExplainerRef.nativeElement.contains( event.target as Node ) ) {

			this.explainerOpen = false;
		}
	}

	@HostListener( 'document:keydown.escape' )
	public handleExplainerEscape(): void {

		if( this.explainerOpen ) {

			this.explainerOpen = false;

			// Focus may have been inside the panel that is now removed
			this.verifiedExplainerTriggerRef?.nativeElement.focus();
		}
	}

	public onSubmit() {

		const selectedCount = this.allMovers.controls.filter( c => c.get( 'checkbox' )?.value ).length;
		this.formService.trackEventWithContext( 'Prematch - Continue', 'consentMovers', {
			selected_count: selectedCount
		} );
		this.consentMovers.markAllAsTouched();
		this.formService.submitFormGroup( this.consentMovers );
	}

	public updateDisplayedMovers() {

		const allMovers = this.allMovers;

		// Get all controls and sort them: selected first, then unselected
		const sortedControls = [...allMovers.controls].map((control, index) => ({ control, index }))
			.sort((a, b) => {
				const aSelected = a.control.get('checkbox')?.value;
				const bSelected = b.control.get('checkbox')?.value;
				return bSelected ? 1 : aSelected ? -1 : 0;
			});

		// Take the first 5 indices
		this.displayedIndices = sortedControls
			.slice(0, this.maxDisplayed)
			.map(({ index }) => index);
	}

	get allMovers(): UntypedFormArray {
		return this.consentMovers.get('movers') as UntypedFormArray || [];
	}

	public openModal() {
		console.log( 'Opening movers consent popup' );

        const dialogConfig = new MatDialogConfig();
		// dialogConfig.height = 'calc( 100vh - 305px )';
		dialogConfig.maxHeight = '850px';
		dialogConfig.width = '940px';
		dialogConfig.backdropClass = 'modal-blurred-bg';
		dialogConfig.panelClass = ['modal-dialog','prematch-dialog'];

		// Get just the values from the movers array.
		// getRawValue() keeps every field the FormService put on the group, so the modal
		// never needs to know which fields exist.
		const moverValues = this.allMovers.controls.map( control => control.getRawValue() );

		// Pass just the values to the modal
		dialogConfig.data = { moverValues };

	    const dialogRef = this.dialog.open(ConsentMoversModalComponent, dialogConfig);

		dialogRef.afterClosed().subscribe( result => {

			if( result !== true ) {

				this.formService.trackEventWithContext( 'Prematch - Cancel modal', 'consentMovers' );
			}

			if( result ) {

				// Update the displayed movers after modal confirmation
				this.updateDisplayedMovers();
			}
		} );
	}
}
